import React, { useContext, useMemo } from 'react';
import { getLocale, useIntl } from 'umi';
import moment from 'moment';
import { TableColumn } from 'tea-component';
import {
	commonValueFormatter,
	formatExeclCommon,
	formatExeclDecimal,
	formatNum,
	getChartDateFormatString
} from '@/pages/dashboard/utils/utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { useSelectorMap, useWeekDay } from '@/pages/dashboard/utils/customHook';
import { getMonetUnit } from '@/pages/dashboard/utils/moneyUnit';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import Revenue from './Revenue';

interface listItem {
	key?: string;
	value?: string;
	[key: string]: any;
}

// 格式化横坐标 label
const axisLabelFormat = (opt, timeType) => {
	return opt.xAxis?.map(item => {
		return {
			...item,
			axisLabel: {
				...item.axisLabel,
				formatter: value => getChartDateFormatString(timeType, value)
			}
		};
	});
};

const getSumValue = (arr: any[] = []) => {
	const sumValue = arr?.reduce((pre, cur) => {
		if (!isNaN(parseFloat(cur.value))) pre += parseFloat(cur.value);
		return pre;
	}, 0);
	return sumValue || '0.00';
};

const formatterFunc = (params, timeType = 'Day', formatMessage, type, decimal) => {
	return getEchartTooltipText({
		title: moment(`${params[0].axisValue}`).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD'),
		metricsName: formatMessage({ id: 'monetization_revenue_Revenue($)' }, { unit: getMonetUnit() }),
		hasSum: true,
		sumName: formatMessage({ id: 'monetization_revenue_Revenue' }),
		sumNum: commonValueFormatter(type, getSumValue(params), decimal),
		data: [...params]
			.sort((f, s) => s.value - f.value)
			.map(({ seriesName, value, color }) => ({
				name: seriesName,
				metricsNum: commonValueFormatter(type, value, decimal),
				color
			})),
		isRemoveNull: true,
		isSort: true
	});
};

function RevenueChannelDistribution(props: any) {
	const contextObj = useContext(FilterContext);
	const { data: defaultData = [], enName, cnName, defaultActive = 1, config: configs } = props;
	const { filterVal: formValue } = contextObj || {};
	const {
		chart: { type, decimal }
	} = configs;
	const { data: dataSource, loading, errorMsg: error } = defaultData?.MonetizationRevenueData || {};
	const data = dataSource?.revenue_channels;
	const { getSelectorText } = useSelectorMap();
	const { getWeekDay } = useWeekDay();
	const { formatMessage } = useIntl();
	const { timeType = 'Day' } = formValue;
	const config = {
		bar: {
			formatterFunc: params => formatterFunc(params, timeType, formatMessage, type, decimal),
			setOtherConfigFunc: opt => {
				opt = {
					...opt,
					xAxis: axisLabelFormat(opt, timeType)
				};
				return opt;
			}
		},
		line: {
			formatterFunc: params => formatterFunc(params, timeType, formatMessage, type, decimal),
			setOtherConfigFunc: opt => {
				opt = {
					...opt,
					xAxis: axisLabelFormat(opt, timeType)
				};
				opt.series = opt.series?.map(item => {
					return {
						...item,
						stack: '',
						showSymbol: true
					};
				});
				return opt;
			}
		}
	};
	const revenueChannelDistributionData = useMemo(() => {
		let revenueData = data ?? [];
		revenueData = revenueData.map((item: any) => {
			return {
				...item,
				revenue: getSumValue(item?.kv_list)
			};
		});
		return revenueData;
	}, [data]);
	const options = useMemo(() => {
		const rc_list: Array<listItem> = revenueChannelDistributionData;
		// 图表数组
		let chart: any[] = [];
		rc_list.forEach(cur => {
			let { date, kv_list = [] } = cur;
			kv_list?.forEach(item => {
				const { key, value } = item;
				const it = chart.find(({ name }) => name === getSelectorText('channel', key));
				if (!it) {
					chart.push({
						name: getSelectorText('channel', key),
						stack: 'bar',
						data: [
							{
								x: date,
								y: value
							}
						]
					});
				} else {
					it?.data?.push({
						x: date,
						y: value
					});
				}
			});
		});

		// 表头固定列
		const tabbleOption: TableColumn[] = [
			{
				key: 'date',
				align: 'left',
				fixed: 'left',
				width: 160,
				header: formatMessage({ id: 'monetization_revenue_Date' }),
				render: (cvm: any) => {
					const weekDay = getWeekDay(cvm?.date);
					const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
					return (
						<>
							<span className='table-text'>{getChartDateFormatString(timeType, cvm?.date, true)}</span>
							{timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
						</>
					);
				}
			},
			{
				key: 'revenue',
				align: 'right',
				fixed: 'left',
				width: 150,
				header: formatMessage({ id: 'monetization_revenue_Revenue($)' }, { unit: getMonetUnit() }),
				render: cvm => {
					return <span>{cvm.revenue}</span>;
				}
			}
		];
		// 表格数据设置表头->取rc_list列表的第0项数据的rc_list
		const { kv_list = [] } = rc_list?.[0] || {};
		const setMap = new Set();
		kv_list?.forEach(({ key }) => {
			setMap.add(key);
		});
		// 表头名称
		const headerNames = [...setMap];
		// 表头非固定列
		headerNames.reduce((pre: any, key: any) => {
			pre.push({
				key,
				align: 'right',
				width: 150,
				header: getSelectorText('channel', key),
				render: cvm => <span>{cvm[key]}</span>
			});
			return pre;
		}, tabbleOption);
		// 表数据
		const tabbleData = rc_list.reduce((outPre, outCur) => {
			const { date = '', revenue = '0.00', kv_list = [] } = outCur;
			// row所对应的所有的column数据key->value
			const columnObj = kv_list?.reduce((pre, { key, value }) => {
				// eslint-disable-next-line no-prototype-builtins
				if (!pre.hasOwnProperty(key)) pre[key] = formatNum(value);
				return pre;
			}, {} as any);
			outPre.push({
				date: getChartDateFormatString(timeType, date, true),
				revenue: formatNum(revenue),
				...columnObj
			});
			return outPre;
		}, [] as any[]);
		const downloadData = rc_list.reduce((outPre, outCur) => {
			const { date = '', revenue = '0.00', kv_list = [] } = outCur;
			// row所对应的所有的column数据key->value
			const columnObj = kv_list?.reduce((pre, { key, value }) => {
				// eslint-disable-next-line no-prototype-builtins
				if (!pre.hasOwnProperty(key)) pre[key] = formatExeclCommon(value);
				return pre;
			}, {} as any);
			outPre.push({
				date: getChartDateFormatString(timeType, date, true),
				revenue: formatExeclDecimal(revenue),
				...columnObj
			});
			return outPre;
		}, [] as any[]);
		return {
			chart,
			table: {
				option: tabbleOption,
				data: tabbleData,
				downloadData
			}
		};
	}, [revenueChannelDistributionData, getLocale()]);
	return (
		<Revenue
			{...{
				otherOption: options,
				data: {
					MonetizationRevenueData: {
						data: {
							revenue: revenueChannelDistributionData
						},
						loading,
						error
					}
				},
				enName,
				cnName,
				otherConfig: config,
				defaultActive,
				isShowLegend: true,
				isHideEyesIcon: true
			}}
		/>
	);
}

export default RevenueChannelDistribution;
